// Pure logic behind the data table (no React, no DOM), so it runs under Vitest. Where a rule copies
// mantine-datatable or the old SharedDataTable, the comment says so: converted pages must behave the same.
import type { DataTableColumn, DataTableSortStatus } from './types';

/** The old SharedDataTable's page sizes. */
export const PAGE_SIZES = [10, 20, 30, 40, 50, 100, 200, 300];

/** The value at a key or path: `posTarif.code`, `items[0].name`. Splits like mantine's `getValueAtPath`. */
export function getValueAtPath(record: unknown, path: string): unknown {
  const parts = path.match(/[^[.\]]+/g);
  if (!parts) return undefined;
  let value: unknown = record;
  for (const part of parts) {
    // mantine's `n && n[a]`: a falsy step (null, undefined, 0, '') ends the walk and is the result.
    if (!value) return value;
    value = (value as Record<string, unknown>)[part];
  }
  return value;
}

/** mantine's default header for a column without a title: `motType` → "Mot type", `no_aju` → "No aju". */
export function humanize(accessor: string): string {
  const words = accessor
    .replace(/([a-z\d])([A-Z]+)/g, '$1 $2')
    .replace(/\W|_/g, ' ')
    .trim()
    .toLowerCase();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}

/** Column ids for TanStack: the accessor, with `__<index>` added when an accessor repeats. */
export function getColumnIds<T>(columns: DataTableColumn<T>[]): string[] {
  const seen = new Map<string, number>();
  return columns.map((column, index) => {
    const id = String(column.accessor);
    const count = seen.get(id) ?? 0;
    seen.set(id, count + 1);
    return count === 0 ? id : `${id}__${index}`;
  });
}

export function getPageCount(totalRecords: number, pageSize: number): number {
  if (totalRecords <= 0 || pageSize <= 0) return 0;
  return Math.ceil(totalRecords / pageSize);
}

/**
 * The footer's "from – to" numbers, as mantine computes them: `to` counts the rows actually on the page,
 * not `page * pageSize`. Null while there is nothing to count (the footer then shows the loading or
 * no-records text instead).
 */
export function getRecordRange({
  page,
  pageSize,
  totalRecords,
  recordsOnPage,
}: {
  page: number;
  pageSize: number;
  totalRecords: number;
  recordsOnPage: number;
}): { from: number; to: number } | null {
  if (!totalRecords) return null;
  const from = (page - 1) * pageSize + 1;
  return { from, to: from + recordsOnPage - 1 };
}

export type PageItem = number | 'ellipsis-start' | 'ellipsis-end';

const range = (from: number, to: number) => Array.from({ length: Math.max(to - from + 1, 0) }, (_, i) => from + i);

/**
 * The page links: first and last page, the current page with `siblings` either side, ellipses for the
 * gaps. The count stays the same wherever the current page is (a one-page gap shows the page, not an
 * ellipsis), so the bar does not jump. MUI's usePagination algorithm with one boundary page.
 */
export function getPageItems(current: number, pageCount: number, siblings = 1): PageItem[] {
  if (pageCount <= 0) return [];
  const page = Math.min(Math.max(current, 1), pageCount);
  const endPages = range(Math.max(pageCount, 2), pageCount);
  const siblingsStart = Math.max(Math.min(page - siblings, pageCount - siblings * 2 - 2), 3);
  const siblingsEnd = Math.min(
    Math.max(page + siblings, siblings * 2 + 3),
    endPages.length > 0 ? pageCount - 2 : pageCount - 1
  );

  const items: PageItem[] = [1];
  if (siblingsStart > 3) items.push('ellipsis-start');
  else if (2 < pageCount - 1) items.push(2);
  items.push(...range(siblingsStart, siblingsEnd));
  if (siblingsEnd < pageCount - 2) items.push('ellipsis-end');
  else if (pageCount - 1 > 1) items.push(pageCount - 1);
  items.push(...endPages);
  return items;
}

/** One page of `data`, for `localPagination` (the old wrapper's slice). */
export function slicePage<T>(data: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return data.slice(start, start + pageSize);
}

/** The page-size choices: the given list (default PAGE_SIZES) plus the current size, sorted, no repeats. */
export function normalizePageSizeOptions(options: number[] | undefined, pageSize: number): number[] {
  const list = new Set((options ?? PAGE_SIZES).filter((n) => Number.isFinite(n) && n > 0));
  if (pageSize > 0) list.add(pageSize);
  return [...list].sort((a, b) => a - b);
}

/**
 * Stable row keys without an explicit `getRowKey`: the old SharedDataTable's `idAccessor` chain, then one
 * generated key per record object (kept in a WeakMap, so the same object keeps its key across renders).
 * Create one per table instance.
 */
export function createRowKeyGetter<T>(): (record: T) => string {
  const generated = new WeakMap<object, string>();
  let counter = 0;
  return (record: T) => {
    const r = record as Record<string, unknown>;
    const has = (key: string) => key in r && r[key] !== null && r[key] !== undefined;
    if (has('_uniqueId')) return String(r._uniqueId);
    if (has('id')) return `id-${String(r.id)}`;
    if (has('seriBarang')) return `barang-${String(r.seriBarang)}`;
    if (has('seriKemasan')) return `kemasan-${String(r.seriKemasan)}`;
    if (has('seriKontainer')) return `petikemas-${String(r.seriKontainer)}`;
    if (has('serial')) return `document-${String(r.serial)}`;
    if ('code' in r && 'name' in r && !('seri' in r)) return `bank-${String(r.code)}`;
    if (has('seri')) return `seri-${String(r.seri)}`;
    if (typeof record !== 'object' || record === null) return `value-${String(record)}`;
    const existing = generated.get(record);
    if (existing) return existing;
    counter += 1;
    const key = `row-${counter}`;
    generated.set(record, key);
    return key;
  };
}

/** A row checkbox: removes the record when it is selected, appends it otherwise (mantine). */
export function toggleRecordSelection<T>(selected: T[], record: T, getKey: (record: T) => string): T[] {
  const key = getKey(record);
  return selected.some((r) => getKey(r) === key) ? selected.filter((r) => getKey(r) !== key) : [...selected, record];
}

export type PageSelectionState = 'none' | 'some' | 'all';

/** The header checkbox state, over the selectable rows of the current page only. */
export function getPageSelectionState<T>(
  selected: T[],
  pageRecords: T[],
  getKey: (record: T) => string,
  isSelectable: (record: T, index: number) => boolean = () => true
): PageSelectionState {
  const selectable = pageRecords.filter(isSelectable);
  if (selectable.length === 0) return 'none';
  const keys = new Set(selected.map(getKey));
  const count = selectable.filter((r) => keys.has(getKey(r))).length;
  if (count === 0) return 'none';
  return count === selectable.length ? 'all' : 'some';
}

/**
 * The header checkbox: when every selectable row on the page is selected, unselects them; otherwise selects
 * them all. Selections on other pages stay (mantine's uniqBy([...selected, ...page]) / filter).
 */
export function togglePageSelection<T>(
  selected: T[],
  pageRecords: T[],
  getKey: (record: T) => string,
  isSelectable: (record: T, index: number) => boolean = () => true
): T[] {
  const selectable = pageRecords.filter(isSelectable);
  const pageKeys = new Set(selectable.map(getKey));
  if (getPageSelectionState(selected, pageRecords, getKey, isSelectable) === 'all') {
    return selected.filter((r) => !pageKeys.has(getKey(r)));
  }
  const result = [...selected];
  const keys = new Set(selected.map(getKey));
  for (const record of selectable) {
    const key = getKey(record);
    if (!keys.has(key)) {
      keys.add(key);
      result.push(record);
    }
  }
  return result;
}

/**
 * A click on a sortable header (mantine): the same column flips direction; another column keeps the
 * current direction (ascending when nothing is sorted yet).
 */
export function nextSortStatus<T>(
  current: DataTableSortStatus<T> | undefined,
  columnAccessor: DataTableSortStatus<T>['columnAccessor'],
  sortKey?: string
): DataTableSortStatus<T> {
  const direction =
    current?.columnAccessor === columnAccessor
      ? current.direction === 'asc'
        ? 'desc'
        : 'asc'
      : current?.direction ?? 'asc';
  return sortKey === undefined ? { columnAccessor, direction } : { columnAccessor, direction, sortKey };
}

/** One entry of mantine-datatable's `<key>-columns-toggle` localStorage value. */
interface StoredColumnToggle {
  accessor: string;
  defaultToggle?: boolean;
  toggleable?: boolean;
  toggled?: boolean;
}

/** The column visibility a table starts with: `defaultToggle === false` hides a toggleable column. */
export function defaultColumnVisibility<T>(columns: DataTableColumn<T>[]): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};
  for (const column of columns) {
    if (column.toggleable) visibility[String(column.accessor)] = column.defaultToggle ?? true;
  }
  return visibility;
}

/**
 * Reads column visibility from a stored `<key>-columns-toggle` value (mantine-datatable's shape, so a key
 * written by the old table still reads). Unknown accessors and non-toggleable columns are ignored; a
 * missing or broken value gives the defaults.
 */
export function readColumnToggle<T>(raw: string | null, columns: DataTableColumn<T>[]): Record<string, boolean> {
  const visibility = defaultColumnVisibility(columns);
  if (!raw) return visibility;
  let stored: unknown;
  try {
    stored = JSON.parse(raw);
  } catch {
    return visibility;
  }
  if (!Array.isArray(stored)) return visibility;
  for (const entry of stored as StoredColumnToggle[]) {
    if (!entry || typeof entry.accessor !== 'string' || typeof entry.toggled !== 'boolean') continue;
    if (entry.accessor in visibility) visibility[entry.accessor] = entry.toggled;
  }
  return visibility;
}

/** The `<key>-columns-toggle` value for a visibility map, in mantine-datatable's shape. */
export function serializeColumnToggle<T>(visibility: Record<string, boolean>, columns: DataTableColumn<T>[]): string {
  const entries: StoredColumnToggle[] = columns.map((column) => {
    const accessor = String(column.accessor);
    const toggleable = Boolean(column.toggleable);
    return {
      accessor,
      defaultToggle: column.defaultToggle ?? true,
      toggleable,
      toggled: toggleable ? visibility[accessor] ?? column.defaultToggle ?? true : true,
    };
  });
  return JSON.stringify(entries);
}
